import { useState } from 'react';
import {
  Modal,
  ModalBody,
  Alert,
  Button,
  Checkbox,
  Card,
  CardBody,
} from '@kollektive/design-system';
import { CheckCircle, Copy, Check } from 'lucide-react';
import type { CreateTokenResponse } from '@/types';
import styles from './TokenCreatedModal.module.css';

interface TokenCreatedModalProps {
  token: CreateTokenResponse;
  onClose: () => void;
}

export function TokenCreatedModal({ token, onClose }: TokenCreatedModalProps) {
  const [copied, setCopied] = useState(false);
  const [acknowledged, setAcknowledged] = useState(false);

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(token.token);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      // Fallback for older browsers
      const textarea = document.createElement('textarea');
      textarea.value = token.token;
      document.body.appendChild(textarea);
      textarea.select();
      document.execCommand('copy');
      document.body.removeChild(textarea);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  const handleClose = () => {
    if (acknowledged) {
      onClose();
    }
  };

  return (
    <Modal open={true} onClose={handleClose} size="md" closeOnBackdropClick={false}>
      <ModalBody>
        <div className={styles.content}>
          {/* Success icon */}
          <div className={styles.iconWrapper}>
            <CheckCircle size={24} className={styles.successIcon} />
          </div>

          <h2 className={styles.title}>Token Created Successfully</h2>

          <p className={styles.description}>
            Your new token <strong>{token.name}</strong> has been created.
          </p>

          {/* Warning banner */}
          <Alert variant="warning" styleVariant="glass" title="Copy this token now">
            This is the only time you'll see this token. Store it securely - you won't be able to view it again.
          </Alert>

          {/* Token display */}
          <Card>
            <CardBody>
              <div className={styles.tokenDisplay}>
                <code className={styles.tokenCode}>{token.token}</code>
                <Button
                  size="sm"
                  onClick={handleCopy}
                  iconLeft={copied ? <Check size={16} /> : <Copy size={16} />}
                >
                  {copied ? 'Copied!' : 'Copy'}
                </Button>
              </div>
            </CardBody>
          </Card>

          {/* Acknowledgment checkbox */}
          <div className={styles.acknowledgment}>
            <Checkbox
              checked={acknowledged}
              onChange={(e) => setAcknowledged(e.target.checked)}
              label="I have copied and securely stored this token. I understand it cannot be recovered."
            />
          </div>

          {/* Close button */}
          <Button onClick={handleClose} disabled={!acknowledged} style={{ width: '100%' }}>
            Done
          </Button>
        </div>
      </ModalBody>
    </Modal>
  );
}
